import { motion } from 'framer-motion';
import { Calendar, Clock, Users, Phone, Mail, MessageSquare, CheckCircle, Calculator, ArrowRight, Star, Quote } from 'lucide-react';
import ContactForm from '../components/contact/ContactForm';
import CalendlyEmbed from '../components/contact/CalendlyEmbed';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';

export default function ContactPage() {

  useSeo({
    title: 'Contact & Book a Call | Point Break Systems',
    description: 'Book a free 30-minute strategy call to discuss AI voice caller deployment. Discuss your lead follow-up, CRM and deployment options. No obligation.',
    path: '/contact',
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Point Break Systems",
        "description": "Book a free consultation to discuss AI voice caller deployment for your real estate business",
        "url": "https://pointbreak.systems/contact",
        "mainEntity": {
          "@type": "Organization",
          "name": "Point Break Systems",
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "telephone": "+17096498492",
            "email": "noah@pointbreak.systems"
          }
        }
      },
    ],
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white relative">
      <div className="relative z-10 pt-6 sm:pt-10">

        {/* Mobile phone banner */}
        <div className="md:hidden mx-4 mb-4 rounded-xl bg-gray-900 text-white flex items-center justify-center gap-3 py-3 px-4">
          <Phone className="w-4 h-4 text-[#d4a10d] flex-shrink-0" />
          <span className="text-sm">Prefer to talk now?&nbsp;</span>
          <a
            href="tel:7096498492"
            className="text-[#d4a10d] font-bold text-sm underline underline-offset-2"
          >
            (709) 649-8492
          </a>
        </div>

        {/* Hero Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center mb-6 sm:mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl lg:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Book Your AI Strategy Call
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto"
          >
            Get a custom demo and deployment plan tailored to your business
          </motion.p>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-8 pb-20">

          {/* Calendly Inline Embed, shown first on all screens */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Header strip */}
              <div className="bg-gradient-to-r from-[#d4a10d] via-[#c49e0c] to-[#b8900b] px-4 py-4 sm:px-8 sm:py-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                  Schedule Your Strategy Call
                </h2>
                <p className="text-center text-white/90 text-base max-w-xl mx-auto">
                  Pick a time that works. 15 to 30 minutes, no obligation
                </p>

                <div className="hidden sm:flex flex-wrap justify-center gap-4 mt-5">
                  <div className="flex items-center gap-2 bg-white/15 rounded-lg px-4 py-2">
                    <Clock className="w-4 h-4 text-white/80" />
                    <span className="text-white text-sm font-medium">15-30 Min</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/15 rounded-lg px-4 py-2">
                    <Users className="w-4 h-4 text-white/80" />
                    <span className="text-white text-sm font-medium">1-on-1 Expert</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/15 rounded-lg px-4 py-2">
                    <Calendar className="w-4 h-4 text-white/80" />
                    <span className="text-white text-sm font-medium">Live Demo</span>
                  </div>
                </div>
              </div>

              {/* Inline Calendly */}
              <CalendlyEmbed />

              {/* Phone fallback below embed */}
              <div className="px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
                <span className="text-gray-500 text-sm">Prefer to book by phone?</span>
                <a
                  href="tel:7096498492"
                  className="inline-flex items-center gap-2 text-[#d4a10d] font-bold hover:text-[#b8900b] transition-colors text-sm"
                >
                  <Phone className="w-4 h-4" />
                  (709) 649-8492
                </a>
              </div>
            </div>
          </motion.div>

          {/* Social proof, above the Calendly embed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {[
              {
                quote: "Noah was right on point with timing and service. Even after setup his continuing service has been great, super reliable from start to finish.",
                name: "Stacey Price",
                biz: "PriceLock Realty",
                photo: "https://cdn.jsdelivr.net/gh/Pointbreakinc/cacherapids-media-host/stacey.jpg",
                initials: "SP",
              },
              {
                quote: "My phone never goes unanswered anymore. Inbound calls are filtered through the AI assistant so I can focus on the highest-value parts of my business. The outbound calling instantly follows up with new leads without everything relying on me personally.",
                name: "Curtis Prokopchuk",
                biz: "REALTOR® | eXp Realty",
                photo: "https://cdn.jsdelivr.net/gh/Pointbreakinc/cacherapids-media-host/curtis.jpg",
                initials: "CP",
              },
            ].map(({ quote, name, biz, photo, initials }) => (
              <div key={name} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#d4a10d] text-[#d4a10d]" />)}
                </div>
                <Quote className="w-5 h-5 text-[#d4a10d]/30 mb-2" />
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic line-clamp-3 sm:line-clamp-none">"{quote}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={photo}
                    alt={name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-[#d4a10d]/30"
                    onError={(e) => {
                      const t = e.currentTarget;
                      t.style.display = 'none';
                      t.nextElementSibling?.removeAttribute('style');
                    }}
                  />
                  <div
                    style={{ display: 'none' }}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#d4a10d] to-[#b8900b] flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  >
                    {initials}
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{name}</p>
                    <p className="text-gray-500 text-xs">{biz}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* What We'll Cover */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-5 sm:p-8 md:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5 sm:mb-8 text-center">
                What We'll Cover in Your Call
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { text: "How this integrates with your CRM" },
                  { text: "Custom deployment for your operation" },
                  { text: "Timeline to go live" },
                  { text: "Pricing for your volume" }
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-start space-x-4 p-4 rounded-lg bg-gradient-to-br from-amber-50 to-gray-50 hover:from-amber-100 hover:to-gray-100 transition-colors duration-200"
                  >
                    <div className="text-[#d4a10d] mt-0.5 flex-shrink-0">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <span className="text-gray-800 font-medium text-lg">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ROI Calculator */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <div className="bg-gradient-to-br from-amber-50 to-gray-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 sm:p-8 md:p-10 border border-amber-100">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Calculator className="w-8 h-8 text-[#d4a10d]" />
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Calculate Your ROI</h3>
              </div>
              <p className="text-gray-700 mb-6 text-center text-lg max-w-2xl mx-auto">
                Want to see the numbers first? Calculate your potential monthly gains and return on investment.
              </p>
              <Link to="/roi-calculator" className="block">
                <motion.button
                  className="w-full md:w-auto mx-auto flex items-center justify-center space-x-3 px-8 py-4 bg-[#d4a10d] hover:bg-[#b8940b] text-white rounded-xl transition-all duration-200 font-bold text-lg shadow-lg hover:shadow-xl group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Calculator className="w-6 h-6" />
                  <span>Try ROI Calculator</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </motion.button>
              </Link>
            </div>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="relative flex items-center justify-center py-4"
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative bg-white px-6 py-2 rounded-full">
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wide">Or</span>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-5 sm:p-8 md:p-10 border border-gray-100">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <MessageSquare className="w-7 h-7 text-[#d4a10d]" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Send Us a Message
                </h2>
              </div>

              <p className="text-gray-600 mb-8 text-center text-lg">
                Prefer to reach out directly? Tell us about your operation and we'll get back to you within 24 hours.
              </p>

              <ContactForm />
            </div>
          </motion.div>

          {/* Contact details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="text-center pt-4 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <div className="flex items-center min-w-0 w-full sm:w-auto space-x-3 px-4 sm:px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-[#d4a10d]/50">
              <Mail className="w-5 h-5 text-[#d4a10d]" />
              <a
                href="mailto:noah@pointbreak.systems"
                className="text-gray-700 hover:text-[#d4a10d] transition-colors font-medium text-base break-all"
              >
                noah@pointbreak.systems
              </a>
            </div>
            <div className="flex items-center min-w-0 w-full sm:w-auto space-x-3 px-4 sm:px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-[#d4a10d]/50">
              <Phone className="w-5 h-5 text-[#d4a10d]" />
              <a
                href="tel:7096498492"
                className="text-gray-700 hover:text-[#d4a10d] transition-colors font-medium text-base break-all"
              >
                (709) 649-8492
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
